import ChannelHeader from "../components/organisms/ChannelHeader";
import { PageContainer } from "@/components/PageContainer";
import { useGetChannelLeaders } from "../hooks/useGetChannelLeaders";
import { Navigate, useParams, Link } from "react-router-dom";
import { ChannelDetailsMain } from "../components/organisms/ChannelDetailsMain";
import ChannelDetailsSkeleton from "../components/organisms/ChannelDetailsSkeleton";
import OnlinePrograms from "@/pages/OnlinePrograms";
import { Box, Divider, Flex, Heading, Text, Button } from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
  useToggleFollowChannel,
  useUserChannelFollowing,
} from "../hooks/useChannelFollowHooks";
import { useUser } from "@/providers/useUser";
import useUpdateChannelLastSeen from "../hooks/useUpdateChannelLastSeen";
import { useLoginModal } from "@/components/Navigation/LoginModal/useLoginModal";
import { learnContext } from "@/pages/academy/Learn/providers/learnProvider";
import GuideTraining from "@/pages/GuideTraining";
import { useGetChannelByUsername } from "../hooks/useGetChannelByUsername";
import { AppRoute } from "@/AppRoute";
import { LiveSessionsCarousel } from "@/features/live-sessions/components/modules/LiveSessionsCarousel";

function ChannelDetailsPage() {
  const { channelUsernameRaw } = useParams<{ channelUsernameRaw?: string }>();
  const channelUsername = channelUsernameRaw?.slice(1);

  const {
    visibleSessions,
    visibleMasterclasses,
    visiblePrograms,
    visibleBootcamps,
    visibleContents,
    setMentorFilterIds,
  } = useContext(learnContext);
  const { user } = useUser();

  const { channel, loading: channelLoading } =
    useGetChannelByUsername(channelUsername);
  const { userChannelFollowing, loading: followLoading } =
    useUserChannelFollowing(channel?.id, user?.id);
  const { leaders, loading: leadersLoading } = useGetChannelLeaders({
    channelId: channel?.id,
  });
  const { updateChannelLastSeen } = useUpdateChannelLastSeen();
  const { openModal: openLoginModal } = useLoginModal();

  const isFollowing = Boolean(
    userChannelFollowing?.channelId === channel?.id &&
      userChannelFollowing?.isFollowing
  );

  const { toggleFollowChannel, loading: followMutationLoading } =
    useToggleFollowChannel();

  useEffect(() => {
    if (channel?.leadersIds?.length) {
      setMentorFilterIds(channel.leadersIds);
    }
    return () => {
      setMentorFilterIds([]);
    };
  }, [channel?.leadersIds, setMentorFilterIds]);

  const hasUpdatedLastSeenRef = useRef(false);
  const previousChannelIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!channel?.id || !user?.id) return;
    // If channel changed, allow updating again
    if (previousChannelIdRef.current !== channel?.id) {
      hasUpdatedLastSeenRef.current = false;
      previousChannelIdRef.current = channel?.id;
    }
    if (hasUpdatedLastSeenRef.current) return;
    updateChannelLastSeen({
      variables: {
        channelId: channel?.id,
      },
    });
    hasUpdatedLastSeenRef.current = true;
  }, [channel?.id, user?.id, updateChannelLastSeen]);

  const isLoading = channelLoading || followLoading || leadersLoading;

  const handleToggleFollow = useCallback(
    async (id: string) => {
      if (!user?.id || !id || followMutationLoading) return;
      await toggleFollowChannel({
        variables: { channelId: id },
      });
    },
    [followMutationLoading, toggleFollowChannel, user?.id]
  );

  const handleLoginRequiredThenFollow = useCallback(
    async (id: string) => {
      const modalUser = await openLoginModal({ showLoginForm: true });
      if (!modalUser || !modalUser.id || !id) return;
      await toggleFollowChannel({ variables: { channelId: id } });
    },
    [openLoginModal, toggleFollowChannel]
  );

  const hayAnyBootcamp = useMemo(() => {
    return (
      visibleBootcamps.cta.length > 0 ||
      visibleBootcamps.myBootcamps.length > 0 ||
      visibleBootcamps.completed.length > 0
    );
  }, [visibleBootcamps]);

  if (!channelUsernameRaw?.startsWith("@"))
    return <Navigate to="/404" replace />;

  if (isLoading && !channel) {
    return (
      <PageContainer w="full">
        <ChannelDetailsSkeleton />
      </PageContainer>
    );
  }

  if (!channel) {
    return (
      <PageContainer w="full">
        <Flex align="center" justify="center" minH="340px" textAlign="center">
          <Box maxW="lg" py={10} px={4}>
            <Heading size="md" mb={3}>
              Canal no encontrado
            </Heading>
            <Text mb={6}>
              No pudimos encontrar el canal que buscas. Verifica el enlace o
              explora otros canales.
            </Text>
            <Link to={AppRoute.Channels}>
              <Button>Ver canales</Button>
            </Link>
          </Box>
        </Flex>
      </PageContainer>
    );
  }

  return (
    <PageContainer w="full">
      <ChannelHeader coverUrl={channel.coverUrl} />
      <ChannelDetailsMain
        channel={channel}
        isFollowing={isFollowing}
        onToggleFollow={handleToggleFollow}
        isFollowLoading={followMutationLoading}
        leaders={leaders}
        leadersLoading={leadersLoading}
        isUserLoggedIn={Boolean(user)}
        onLoginRequired={handleLoginRequiredThenFollow}
      />

      <Flex flexDirection="column">
        {visibleSessions.next.length > 0 && (
          <Box>
            {channel?.leadersIds.length > 0 && <LiveSessionsCarousel />}
          </Box>
        )}
        {(visiblePrograms.length > 0 ||
          visibleMasterclasses.length > 0 ||
          visibleContents.length > 0) && (
          <>
            <Divider my="24px" borderBottomWidth="0.5px" />
            <Box>
              <OnlinePrograms showExternal />
            </Box>
          </>
        )}

        {hayAnyBootcamp && (
          <>
            <Divider my={"24px"} borderBottomWidth={"0.5px"} />
            <Box>
              <GuideTraining />
            </Box>
          </>
        )}
      </Flex>
    </PageContainer>
  );
}

export default ChannelDetailsPage;
